import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";
import { getMessages } from "@/i18n/runtime";
import type { MHovViewModel, MHovSnapshot } from "../types";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { applyHovThemeHue } from "../nHov/nHovThemeCss";

export type MHovSpeedSetting = "normal" | "fast" | "instant";

interface MHovViewOptions {
  readonly container: HTMLElement;
  readonly onExecute: () => void | Promise<void>;
  readonly getChargeDurationMs: () => number;
}

export interface MHovViewUpdate extends MHovViewModel {
  readonly themeHue?: number | null;
}

export type MHovChargeState = "idle" | "charging" | "charged" | "executing";

/**
 * MHov button label layout. "compact" is the mobile low-profile execution bar
 * that uses the short `MHov` term; "default" keeps the full desktop wording.
 */
export type MHovButtonLayout = "default" | "compact";

/** Viewport width at and below which the MHov button uses the compact bar. */
const MHOV_COMPACT_LAYOUT_MEDIA_QUERY = "(max-width: 560px)";

/**
 * Picks the MHov button label for the given layout. Pure so the compact/full
 * wording contract can be verified without a DOM or viewport.
 */
export function selectMHovBtnLabel(
  messages: ReturnType<typeof getMessages>,
  layout: MHovButtonLayout,
): (before: string, after: string) => string {
  return layout === "compact"
    ? messages.hovView.mHov.btnLabelMobile
    : messages.hovView.mHov.btnLabel;
}

export class MHovExecutionLock {
  private state: MHovChargeState = "idle";

  getState(): MHovChargeState {
    return this.state;
  }

  canStart(canExecute: boolean): boolean {
    return canExecute && this.state === "idle";
  }

  startCharging(canExecute: boolean): boolean {
    if (!this.canStart(canExecute)) return false;
    this.state = "charging";
    return true;
  }

  markCharged(): boolean {
    if (this.state !== "charging" && this.state !== "idle") return false;
    this.state = "charged";
    return true;
  }

  startExecuting(): boolean {
    if (this.state !== "charged") return false;
    this.state = "executing";
    return true;
  }

  cancelCharging(): boolean {
    if (this.state !== "charging") return false;
    this.state = "idle";
    return true;
  }

  completeExecution(): void {
    this.state = "idle";
  }
}

export class MHovView implements Disposable {
  readonly root: HTMLDivElement;

  private readonly btn: HTMLButtonElement;
  private readonly progressBar: HTMLDivElement;
  private readonly getChargeDurationMs: () => number;
  private readonly onExecute: () => void | Promise<void>;
  private readonly executionLock = new MHovExecutionLock();
  private readonly chargeController: HoverChargeExecutionController;
  private readonly compactLayoutQuery = window.matchMedia(
    MHOV_COMPACT_LAYOUT_MEDIA_QUERY,
  );

  private isHovering = false;
  private isInputLocked = false;
  private lastSnapshot: MHovSnapshot | null = null;

  private readonly handlePointerEnter = (): void => {
    this.isHovering = true;
    this.startHold();
  };

  private readonly handlePointerLeave = (): void => {
    this.isHovering = false;
    this.releaseHold();
  };

  private readonly handleFocus = (): void => {
    if (!this.isHovering) {
      this.isHovering = true;
      this.startHold();
    }
  };

  private readonly handleBlur = (): void => {
    this.isHovering = false;
    this.releaseHold();
  };

  constructor(options: MHovViewOptions) {
    const messages = getMessages();
    this.getChargeDurationMs = options.getChargeDurationMs;
    this.onExecute = options.onExecute;

    this.root = document.createElement("div");
    this.root.className = "mhov-panel";
    this.root.hidden = true;
    this.root.dataset.chargeState = this.executionLock.getState();
    this.root.dataset.executing = "false";
    this.root.dataset.inputLocked = "false";

    this.btn = document.createElement("button");
    this.btn.className = "mhov-btn";
    this.btn.type = "button";
    this.btn.dataset.virtualPointer = "allow";
    this.btn.dataset.chargeState = this.executionLock.getState();
    this.btn.dataset.executing = "false";
    this.btn.setAttribute("aria-label", messages.hovView.mHov.ariaLabel);

    this.progressBar = document.createElement("div");
    this.progressBar.className = "mhov-btn-progress";
    this.progressBar.setAttribute("aria-hidden", "true");

    this.btn.append(this.progressBar);

    this.root.append(this.btn);
    options.container.append(this.root);

    this.chargeController = new HoverChargeExecutionController({
      getDurationMs: () => Math.max(0, this.getChargeDurationMs()),
      canExecute: () =>
        !this.isInputLocked && (this.lastSnapshot?.canExecute ?? false),
      onProgress: (progress) => this.applyChargeProgress(progress),
      onExecute: () => {
        this.markCharged({ animateProgress: false });
        void this.executeOnce();
      },
    });

    this.btn.addEventListener("pointerenter", this.handlePointerEnter);
    this.btn.addEventListener("pointerleave", this.handlePointerLeave);
    this.btn.addEventListener("focus", this.handleFocus);
    this.btn.addEventListener("blur", this.handleBlur);
  }

  update(mv: MHovViewUpdate): void {
    this.lastSnapshot = mv.mHov;
    applyHovThemeHue(this.root, mv.themeHue ?? null);
    this.root.hidden = !mv.mHov.shouldShow;
    if (!mv.mHov.canExecute) {
      this.cancelHold();
    }
    this.btn.disabled =
      !mv.mHov.canExecute ||
      this.executionLock.getState() === "executing" ||
      this.isInputLocked;
    this.btn.dataset.canExecute = mv.mHov.canExecute ? "true" : "false";
    this.syncExecutionState();

    const btnLabelEl = this.btn.querySelector(".mhov-btn-label");
    if (!btnLabelEl) {
      const el = document.createElement("span");
      el.className = "mhov-btn-label";
      this.btn.append(el);
      this.updateBtnLabel(el, mv.mHov);
    } else {
      this.updateBtnLabel(btnLabelEl as HTMLElement, mv.mHov);
    }
  }

  private updateBtnLabel(el: HTMLElement, snapshot: MHovSnapshot): void {
    const messages = getMessages();
    const current = formatForContext(snapshot.hgMultiplier, "compact");
    const fresh = formatForContext(snapshot.pendingMultiplier, "compact");
    const layout: MHovButtonLayout = this.compactLayoutQuery.matches
      ? "compact"
      : "default";
    if (snapshot.freshnessWallActive) {
      const effective = formatForContext(
        snapshot.effectivePendingMultiplier,
        "compact",
      );
      el.textContent = messages.hovView.mHov.btnLabelFreshnessWall(
        current,
        fresh,
        effective,
      );
      return;
    }
    el.textContent = selectMHovBtnLabel(messages, layout)(current, fresh);
  }

  private startHold(): void {
    if (this.isInputLocked) {
      return;
    }
    const state = this.executionLock.getState();
    if (state === "idle") {
      if (
        !this.executionLock.startCharging(
          this.lastSnapshot?.canExecute ?? false,
        )
      ) {
        return;
      }
    } else if (state !== "charging") {
      return;
    }
    this.syncExecutionState();
    this.chargeController.setHovered(true);
  }

  private markCharged(options: { readonly animateProgress: boolean }): void {
    this.executionLock.markCharged();
    this.setProgressWidth("100%", options.animateProgress);
    this.syncExecutionState();
  }

  private async executeOnce(): Promise<void> {
    if (!this.executionLock.startExecuting()) {
      return;
    }
    this.syncExecutionState();
    try {
      await this.onExecute();
    } catch (error) {
      console.error("MHov execution failed", error);
    } finally {
      this.executionLock.completeExecution();
      this.setProgressWidth("0%", false);
      this.syncExecutionState();
    }
  }

  private cancelHold(): void {
    if (!this.executionLock.cancelCharging()) {
      return;
    }
    this.chargeController.cancel();
    this.setProgressWidth("0%", true);
    this.syncExecutionState();
  }

  private releaseHold(): void {
    this.chargeController.setHovered(false);
    this.syncExecutionState();
  }

  private applyChargeProgress(progress: number): void {
    const pct = progress * 100;
    this.progressBar.style.width = `${pct}%`;
    if (
      progress <= 0 &&
      !this.isHovering &&
      this.executionLock.getState() === "charging"
    ) {
      this.executionLock.cancelCharging();
      this.syncExecutionState();
      return;
    }
    this.syncExecutionState();
  }

  private syncExecutionState(): void {
    const state = this.executionLock.getState();
    const executing = state === "executing";
    const chargeState = executing ? "charged" : state;
    this.btn.disabled =
      !(this.lastSnapshot?.canExecute ?? false) ||
      executing ||
      this.isInputLocked;
    this.root.dataset.chargeState = chargeState;
    this.root.dataset.executing = executing ? "true" : "false";
    this.root.dataset.inputLocked = this.isInputLocked ? "true" : "false";
    this.btn.dataset.chargeState = chargeState;
    this.btn.dataset.executing = executing ? "true" : "false";
    this.btn.setAttribute("aria-busy", executing ? "true" : "false");
  }

  private setProgressWidth(width: string, animate: boolean): void {
    this.progressBar.style.transition = animate ? "" : "none";
    this.progressBar.style.width = width;
    if (!animate) {
      requestAnimationFrame(() => {
        this.progressBar.style.transition = "";
      });
    }
  }

  setInputLocked(locked: boolean): void {
    if (locked === this.isInputLocked) {
      return;
    }
    this.isInputLocked = locked;
    if (locked) {
      this.isHovering = false;
      this.cancelHold();
    }
    this.syncExecutionState();
  }

  dispose(): void {
    this.chargeController.dispose();
    this.btn.removeEventListener("pointerenter", this.handlePointerEnter);
    this.btn.removeEventListener("pointerleave", this.handlePointerLeave);
    this.btn.removeEventListener("focus", this.handleFocus);
    this.btn.removeEventListener("blur", this.handleBlur);
    this.root.remove();
  }
}
